/**
 * Facets — version minimale.
 *
 * Web components :
 *   <facets-form-component>  — englobe le formulaire de filtres. Expose
 *                              updateFilters() appelé par chaque input onchange.
 *                              En mode drawer (data-drawer="true"), met à jour
 *                              en partial swap pour ne pas refermer le drawer.
 *   <price-facet-component>  — clampe les bornes min/max du filtre prix avant
 *                              de déclencher la mise à jour.
 */

import { sectionRenderer } from "@theme/section-renderer";
import { Component } from "@theme/component";
import { FilterUpdateEvent } from "@theme/events";
import { convertMoneyToMinorUnits, formatMoney } from "@theme/money-formatting";

const SEARCH_QUERY = "q";

/**
 * @extends {Component<{ facetsForm: HTMLFormElement }>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ["facetsForm"];

  /**
   * Construit les URL search params à partir du formulaire.
   * @param {FormData} [formData]
   * @returns {URLSearchParams}
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    const params = new URLSearchParams(/** @type any */ (formData));
    if (params.get("filter.v.price.gte") === "") params.delete("filter.v.price.gte");
    if (params.get("filter.v.price.lte") === "") params.delete("filter.v.price.lte");
    params.delete("page");

    const searchQuery = new URL(window.location.href).searchParams.get(SEARCH_QUERY) ?? "";
    if (searchQuery) params.set(SEARCH_QUERY, searchQuery);

    return params;
  }

  get sectionId() {
    const id = this.getAttribute("section-id");
    if (!id) throw new Error("section-id required on <facets-form-component>");
    return id;
  }

  /** Met à jour l'URL sans recharger. */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const params = this.createURLParameters();
    url.search = "";
    for (const [k, v] of params.entries()) url.searchParams.append(k, v);
    history.pushState({ urlParameters: params.toString() }, "", url.toString());
  }

  /**
   * Appelé par chaque input onchange. En drawer = swap partiel (le drawer
   * reste en place donc reste ouvert) ; sinon, re-render complet de la section.
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));

    if (this.dataset.drawer === "true") {
      this.#partialUpdate();
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  };

  /** Applique une URL de filtres reçue de l'extérieur (ex: lien "retirer ce filtre"). */
  updateFiltersByURL(url) {
    history.pushState({}, "", url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));

    if (this.dataset.drawer === "true") {
      this.#partialUpdate();
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Drawer mode : on remplace uniquement la grille produits et le <form>.
   * Le wrapper `.filter_drawer` reste intact donc la classe `.is-open`
   * et l'animation de slide-in ne se rejouent pas.
   */
  async #partialUpdate() {
    const url = new URL(window.location.href);
    url.searchParams.set("section_id", this.sectionId);

    try {
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      // Grille produits.
      const newMain = doc.querySelector(".collection__main");
      const currentMain = document.querySelector(".collection__main");
      if (newMain && currentMain) currentMain.replaceWith(newMain);

      // Formulaire de filtres (états actifs, counts) — le drawer wrapper
      // reste en place.
      const newForm = doc.querySelector(".filter_form");
      const currentForm = this.querySelector(".filter_form");
      if (newForm && currentForm) currentForm.replaceWith(newForm);
    } catch (err) {
      console.error("[facets] partial update failed, falling back to full render:", err);
      sectionRenderer.renderSection(this.sectionId);
    }
  }
}

if (!customElements.get("facets-form-component")) {
  customElements.define("facets-form-component", FacetsFormComponent);
}

/**
 * @extends {Component<{ minInput: HTMLInputElement, maxInput: HTMLInputElement }>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */ currency;
  /** @type {string} */ moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keydown", this.#onKeyDown);
    this.currency = this.dataset.currency ?? "USD";
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? "{{amount}}");
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keydown", this.#onKeyDown);
  }

  #extractMoneyPlaceholder(format) {
    const m = format.match(/{{\s*\w+\s*}}/);
    return m ? m[0] : "{{amount}}";
  }

  /** Restreint la saisie au numérique + touches de navigation. */
  #onKeyDown = (event) => {
    if (event.metaKey) return;
    const allowed = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(allowed)) event.preventDefault();
  };

  /** Appelé par onchange des inputs prix. Clamp puis update du form parent. */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;
    this.#clampInput(minInput);
    this.#clampInput(maxInput);

    const form = this.closest("facets-form-component");
    if (form instanceof FacetsFormComponent) form.updateFilters();
  }

  #clampInput(input) {
    if (input.value.trim() === "") return;
    const value = convertMoneyToMinorUnits(input.value, this.currency) ?? 0;
    const min = convertMoneyToMinorUnits(input.getAttribute("data-min") ?? "0", this.currency) ?? 0;
    const max = convertMoneyToMinorUnits(input.getAttribute("data-max") ?? "0", this.currency) ?? 0;
    if (value < min) input.value = formatMoney(min, this.moneyFormat, this.currency);
    else if (value > max) input.value = formatMoney(max, this.moneyFormat, this.currency);
  }
}

if (!customElements.get("price-facet-component")) {
  customElements.define("price-facet-component", PriceFacetComponent);
}
